"use client";

import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPanchang } from "@/lib/api/panchang";
import { PanchangCityProvider, usePanchangCity } from "@/contexts/PanchangCity";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import { todayStr } from "./_data";
import { SaturnPositionCard, RashiPicker, AffectedRashiList, TransitTimeline } from "./_sections";

export default function ShaniTransitClient() {
  return (
    <PanchangCityProvider>
      <ShaniTransitContent />
    </PanchangCityProvider>
  );
}

function ShaniTransitContent() {
  const { city } = usePanchangCity();
  const dateStr = useMemo(() => todayStr(), []);
  const [selectedRashi, setSelectedRashi] = useState<string | null>(null);

  useEffect(() => {
    trackCalculatorUsed({ calculator: "shani-transit" });
  }, []);

  const { data: panchang } = useQuery({
    queryKey: ["panchang", city.slug, dateStr],
    queryFn: () =>
      getPanchang({
        date: dateStr,
        latitude: city.latitude,
        longitude: city.longitude,
        tz_offset: 5.5,
      }),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const saturnPlanet = panchang?.planets?.find((p) => p.planet_id === "saturn");
  const saturnDegree = saturnPlanet ? saturnPlanet.rashi_degree.toFixed(1) : null;
  const isRetrograde = saturnPlanet?.is_retrograde ?? false;

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-5xl mb-3">♄</div>
        <h1
          className="text-2xl sm:text-3xl font-bold mb-2"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          Shani Transit 2025–2026
        </h1>
        <p className="text-[14px]" style={{ color: "var(--ish-text-muted)" }}>
          Saturn&apos;s current position and which Moon signs are under Sade Sati
        </p>
      </div>

      <SaturnPositionCard saturnDegree={saturnDegree} isRetrograde={isRetrograde} />
      <RashiPicker selectedRashi={selectedRashi} onSelect={setSelectedRashi} />
      <AffectedRashiList />
      <TransitTimeline />

      <CalculatorCrossLinks currentPage="shani-transit" />
    </div>
  );
}
